import { idx, rowsByIndex, store } from "@/store/store";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";

/** A member who can take a handoff, with the name the picker shows. */
export type OfficeMember = { membershipId: string; name: string };

/** The membership roles that work the office; drivers are `limitedMember` and never qualify. */
const OFFICE_ROLES: ReadonlySet<string> = new Set(["member", "admin"]);

/**
 * The workspace's office members, for the reassignment picker.
 *
 * Active memberships with role `member` or `admin`, joined to their account for a first and last name. An account
 * without a name, an account the store marks as a bot, and an invitation no account has claimed are all left out.
 */
export function useOfficeMembers(workspaceId: string): readonly OfficeMember[] {
  return useCollectionsCompute(
    ["workspaceMembership", "account"],
    () =>
      rowsByIndex("workspaceMembership", idx.membershipsByWorkspace, workspaceId).flatMap((membership) => {
        if (membership.status !== "active" || !OFFICE_ROLES.has(membership.role) || !membership.accountId) return [];
        const account = store.get("account", membership.accountId);
        if (!account || account.bot) return [];
        const name = [account.firstName, account.lastName]
          .map((part) => part?.trim())
          .filter(Boolean)
          .join(" ");
        return name ? [{ membershipId: membership.id, name }] : [];
      }),
    [workspaceId],
    "officeMembers",
  );
}
